import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Stack, Tooltip, Chip, Paper, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Checkbox,
  Snackbar, Alert, Collapse,
} from '@mui/material';
import {
  Add as AddIcon, RefreshOutlined, DeleteOutlined, EditOutlined,
  OpenInNewOutlined,
  CloseOutlined, PlayArrowOutlined, PauseOutlined, ExpandMoreOutlined,
  ExpandLessOutlined, SendOutlined, WarningAmberOutlined, ContentCopyOutlined,
  AutorenewOutlined, LockOutlined,
} from '@mui/icons-material';
import { webhooksApi, type WebhookResponse, type WebhookCreatedResponse, type WebhookDeliveryResponse } from '../../api/integrations';
import { PlanFeatureBanner } from '../../components/integrations/PlanFeatureBanner';
import { useApiAccessLevel } from '../../hooks/useApiAccessLevel';
import { useTranslation } from 'react-i18next';
import { activeLocale } from '../../utils/datetime';

type Toast = { message: string; severity: 'success' | 'error' | 'info' } | null;

const EVENT_OPTIONS = [
  // ADR-037 — must match the canonical deliverable set (TENANT_WEBHOOK_EVENT_TYPES
  // business events). These are the events that actually fan out to the webhook.
  { value: 'clock_in_late', i18nKey: 'integrations:webhooks.events.clockInLate' },
  { value: 'leave_request_submitted', i18nKey: 'integrations:webhooks.events.leaveSubmitted' },
  { value: 'leave_request_approved', i18nKey: 'integrations:webhooks.events.leaveApproved' },
  { value: 'leave_request_rejected', i18nKey: 'integrations:webhooks.events.leaveRejected' },
  { value: 'process_request_submitted', i18nKey: 'integrations:webhooks.events.processSubmitted' },
  { value: 'process_request_status_changed', i18nKey: 'integrations:webhooks.events.processStatusChanged' },
  { value: 'shift_assigned', i18nKey: 'integrations:webhooks.events.shiftAssigned' },
];

const Webhooks: React.FC = () => {
  const { t } = useTranslation();
  const [webhooks, setWebhooks] = useState<WebhookResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', url: '', events: [] as string[] });
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [secretDialogOpen, setSecretDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDeliveryResponse[]>([]);
  const [toast, setToast] = useState<Toast>(null);
  const { level, loaded, canRead, canWrite, openUpgrade } = useApiAccessLevel();

  const fetchWebhooks = useCallback(async () => {
    if (!loaded) return;
    if (!canRead) {
      setWebhooks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try { setWebhooks(await webhooksApi.list()); } catch (e) { console.warn('Failed to load webhooks', e); } finally { setLoading(false); }
  }, [loaded, canRead]);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const requireWrite = () => {
    if (canWrite) return true;
    openUpgrade();
    return false;
  };

  const handleOpen = (item?: WebhookResponse) => {
    if (!requireWrite()) return;
    if (item) {
      setEditingId(item.id);
      setForm({ name: item.name, url: item.url, events: item.events });
    } else {
      setEditingId(null);
      setForm({ name: '', url: '', events: [] });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!requireWrite()) return;
    try {
      if (editingId) {
        await webhooksApi.update(editingId, form);
        setToast({ message: t('integrations:webhooks.webhookUpdated'), severity: 'success' });
      } else {
        const result = await webhooksApi.create(form) as WebhookCreatedResponse;
        setCreatedSecret(result.secret);
        setSecretDialogOpen(true);
        setToast({ message: t('integrations:webhooks.webhookCreated'), severity: 'success' });
      }
      setDialogOpen(false);
      fetchWebhooks();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:webhooks.saveFailed'), severity: 'error' });
    }
  };

  const handleToggle = async (wh: WebhookResponse) => {
    if (!requireWrite()) return;
    const newStatus = wh.status === 'active' ? 'paused' : 'active';
    try {
      await webhooksApi.update(wh.id, { status: newStatus });
      fetchWebhooks();
    } catch (e) { console.warn('Failed to toggle webhook', e); }
  };

  const handleDelete = async (wh: WebhookResponse) => {
    if (!requireWrite()) return;
    try {
      await webhooksApi.delete(wh.id);
      setToast({ message: t('integrations:webhooks.webhookDeleted', { name: wh.name }), severity: 'success' });
      fetchWebhooks();
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:webhooks.deleteFailed'), severity: 'error' });
    }
  };

  const handleTest = async (wh: WebhookResponse) => {
    if (!requireWrite()) return;
    try {
      const delivery = await webhooksApi.test(wh.id);
      setToast({ message: delivery.success ? t('integrations:webhooks.testSuccess') : t('integrations:webhooks.testFailed', { status: delivery.response_status }), severity: delivery.success ? 'success' : 'error' });
      if (expandedId === wh.id) loadDeliveries(wh.id);
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:webhooks.testFailedGeneric'), severity: 'error' });
    }
  };

  const handleRotateSecret = async (wh: WebhookResponse) => {
    if (!requireWrite()) return;
    try {
      const result = await webhooksApi.rotateSecret(wh.id) as WebhookCreatedResponse;
      setCreatedSecret(result.secret);
      setSecretDialogOpen(true);
    } catch (e: unknown) {
      setToast({ message: (e as Error).message || t('integrations:webhooks.rotateFailed'), severity: 'error' });
    }
  };

  const loadDeliveries = async (webhookId: string) => {
    try { setDeliveries(await webhooksApi.deliveries(webhookId)); } catch { setDeliveries([]); }
  };

  const toggleExpand = (id: string) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    loadDeliveries(id);
  };

  const toggleEvent = (event: string) => {
    setForm(prev => ({ ...prev, events: prev.events.includes(event) ? prev.events.filter(e => e !== event) : [...prev.events, event] }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setToast({ message: t('integrations:webhooks.copied'), severity: 'info' });
  };

  const activeWebhooks = webhooks.filter((webhook) => webhook.status === 'active').length;
  const eventLabel = (event: string) => {
    const option = EVENT_OPTIONS.find((candidate) => candidate.value === event);
    return option ? t(option.i18nKey) : event;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('integrations:webhooks.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('integrations:webhooks.subtitleDetail')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}><IconButton size="small" onClick={fetchWebhooks} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          <Button variant="contained" size="small" startIcon={canWrite ? <AddIcon /> : <LockOutlined />}
            onClick={() => (canWrite ? handleOpen() : openUpgrade())}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('integrations:webhooks.addWebhook')}</Button>
          {/* HANDOFF_2026-05-25 Bug 26 — top-right View docs button on
              every integration admin page. Routes to the public-facing
              SMB-friendly guide on svdy.com (not the internal swagger). */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewOutlined />}
            component="a"
            href="https://svdy.com/integrations/webhooks"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)', bgcolor: '#ffffff', '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' } }}
          >
            {t('integrations:webhooks.viewDocs')}
          </Button>
        </Stack>
      </Box>

      {/* Inline Stats */}
      {loaded && canRead && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('integrations:webhooks.statsLine', {
            total: webhooks.length,
            active: activeWebhooks,
          })}
        </Typography>
      )}

      {/* Capability is resolved once by useApiAccessLevel and shared with the banner. */}
      <PlanFeatureBanner level={level} loaded={loaded} requires="write" />

      {/* Table */}
      {!loaded || loading ? (
        <Typography variant="body2" color="text.secondary">{t('common:status.loading')}</Typography>
      ) : !canRead ? null : webhooks.length === 0 ? (
        <Paper sx={{ p: 4, width: '100%', textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Typography variant="body2" color="text.secondary">{t('integrations:webhooks.noWebhooks')}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600, fontSize: '0.8rem', py: 0.75 } }}>
              <TableRow>
                <TableCell width={30} />
                <TableCell>{t('integrations:webhooks.colName', { defaultValue: 'Name' })}</TableCell>
                <TableCell>{t('integrations:webhooks.colUrl', { defaultValue: 'URL' })}</TableCell>
                <TableCell>{t('integrations:webhooks.colEvents', { defaultValue: 'Events' })}</TableCell>
                <TableCell align="center">{t('integrations:webhooks.colSuccessRate', { defaultValue: 'Success Rate' })}</TableCell>
                <TableCell align="center">{t('integrations:webhooks.colStatus', { defaultValue: 'Status' })}</TableCell>
                <TableCell align="center">{t('integrations:webhooks.colActions', { defaultValue: 'Actions' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhooks.map(w => (
                <React.Fragment key={w.id}>
                  <TableRow hover>
                    <TableCell sx={{ px: 0.5 }}>
                      <IconButton size="small" onClick={() => toggleExpand(w.id)}>
                        {expandedId === w.id ? <ExpandLessOutlined sx={{ fontSize: 16 }} /> : <ExpandMoreOutlined sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{w.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                        {t('integrations:webhooks.createdOn', { date: w.created_at ? new Date(w.created_at).toLocaleDateString(activeLocale()) : '—' })}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>{w.url}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {w.events.slice(0, 2).map((event) => <Chip key={event} label={eventLabel(event)} size="small" variant="outlined" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />)}
                        {w.events.length > 2 && <Chip label={`+${w.events.length - 2}`} size="small" sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" sx={{ fontWeight: 600, color: (w.success_rate ?? 100) >= 95 ? 'success.main' : (w.success_rate ?? 100) >= 80 ? 'warning.main' : 'error.main' }}>
                        {w.success_rate ?? 100}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={w.status === 'active' ? t('common:status.active') : t('integrations:webhooks.paused')} size="small" variant="outlined"
                        color={w.status === 'active' ? 'success' : 'warning'}
                        sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.25} justifyContent="center">
                        <Tooltip title={t('integrations:webhooks.testTooltip')}><IconButton size="small" onClick={() => handleTest(w)}><SendOutlined sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                        <Tooltip title={w.status === 'active' ? t('integrations:webhooks.pauseTooltip') : t('integrations:webhooks.resumeTooltip')}><IconButton size="small" onClick={() => handleToggle(w)}>{w.status === 'active' ? <PauseOutlined sx={{ fontSize: 15 }} /> : <PlayArrowOutlined sx={{ fontSize: 15 }} />}</IconButton></Tooltip>
                        <Tooltip title={t('integrations:webhooks.rotateSecret')}><IconButton size="small" onClick={() => handleRotateSecret(w)}><AutorenewOutlined sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                        <Tooltip title={t('common:actions.edit')}><IconButton size="small" onClick={() => handleOpen(w)}><EditOutlined sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                        <Tooltip title={t('common:actions.delete')}><IconButton size="small" onClick={() => handleDelete(w)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {/* Delivery Log Expand */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                      <Collapse in={expandedId === w.id} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 3, py: 1.5, bgcolor: 'grey.50' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>{t('integrations:webhooks.recentDeliveries')}</Typography>
                          {deliveries.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">{t('integrations:webhooks.noDeliveries')}</Typography>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>{t('integrations:webhooks.colEvent', { defaultValue: 'Event' })}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>{t('integrations:webhooks.colStatus', { defaultValue: 'Status' })}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>{t('integrations:webhooks.colResponse', { defaultValue: 'Response' })}</TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>{t('integrations:webhooks.colTime', { defaultValue: 'Time' })}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {deliveries.slice(0, 10).map(d => (
                                  <TableRow key={d.id}>
                                    <TableCell sx={{ py: 0.5 }}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{eventLabel(d.event)}</Typography></TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      <Chip label={d.success ? t('integrations:webhooks.ok') : t('common:status.failed')} size="small" color={d.success ? 'success' : 'error'} variant="outlined" sx={{ borderRadius: 1, height: 16, fontSize: '0.6rem' }} />
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{d.response_status || '—'} · {d.response_ms ?? 0}ms</Typography></TableCell>
                                    <TableCell sx={{ py: 0.5 }}><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{d.created_at ? new Date(d.created_at).toLocaleString(activeLocale()) : '—'}</Typography></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {t('integrations:webhooks.signatureTip')}
      </Typography>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{editingId ? t('integrations:webhooks.editWebhook') : t('integrations:webhooks.addWebhook')}</Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseOutlined fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            <TextField label={t('integrations:webhooks.nameLabel')} placeholder={t('integrations:webhooks.namePlaceholder')} value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} size="small" fullWidth required />
            <TextField label={t('integrations:webhooks.urlLabel')} placeholder={t('integrations:webhooks.urlPlaceholder')} value={form.url}
              onChange={e => setForm(p => ({ ...p, url: e.target.value }))} size="small" fullWidth required
              helperText={t('integrations:webhooks.urlHelper')} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 1 }}>{t('integrations:webhooks.eventsToSubscribe')}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                {EVENT_OPTIONS.map((ev, i) => (
                  <Box key={ev.value} onClick={() => toggleEvent(ev.value)} sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, cursor: 'pointer',
                    borderBottom: i < EVENT_OPTIONS.length - 2 ? '1px solid' : 'none', borderColor: 'divider',
                    borderRight: i % 2 === 0 ? '1px solid' : 'none',
                    bgcolor: form.events.includes(ev.value) ? 'primary.50' : 'transparent',
                    transition: 'background-color 80ms ease',
                    '&:hover': { bgcolor: form.events.includes(ev.value) ? 'primary.100' : 'grey.50' },
                  }}>
                    <Checkbox size="small" checked={form.events.includes(ev.value)} sx={{ p: 0 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: form.events.includes(ev.value) ? 600 : 400 }}>{t(ev.i18nKey)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} size="small" sx={{ borderRadius: 1, textTransform: 'none' }}>{t('integrations:webhooks.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} size="small" disabled={!form.name.trim() || !form.url.trim() || form.events.length === 0}
            sx={{ borderRadius: 1, textTransform: 'none' }}>{editingId ? t('integrations:webhooks.saveChanges') : t('integrations:webhooks.addWebhook')}</Button>
        </DialogActions>
      </Dialog>

      {/* Secret Show-Once Dialog */}
      <Dialog open={secretDialogOpen} onClose={() => setSecretDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #e65100, #e65100dd)', color: '#fff', position: 'relative' }}>
          <IconButton size="small" onClick={() => setSecretDialogOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, color: 'inherit', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
            <CloseOutlined fontSize="small" />
          </IconButton>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}>
              <WarningAmberOutlined />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{t('integrations:webhooks.signingSecret')}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>{t('integrations:webhooks.shownOnce')}</Typography>
            </Box>
          </Stack>
        </Box>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{t('integrations:webhooks.yourSigningSecret')}</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all', fontWeight: 600 }}>
              {createdSecret}
            </Typography>
          </Box>
          <Button size="small" startIcon={<ContentCopyOutlined />} onClick={() => createdSecret && copyToClipboard(createdSecret)}
            sx={{ borderRadius: 1, textTransform: 'none', mb: 2 }}>{t('integrations:webhooks.copyToClipboard')}</Button>
          <Box sx={{ p: 1.5, bgcolor: 'warning.50', borderRadius: 1.5, border: '1px solid', borderColor: 'warning.200' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <WarningAmberOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'warning.dark' }}>
                {t('integrations:webhooks.secretHint')}
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button variant="contained" onClick={() => setSecretDialogOpen(false)} size="small"
            sx={{ borderRadius: 1, textTransform: 'none' }}>{t('integrations:webhooks.savedSecret')}</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Webhooks;
